import React, { useState, useEffect } from "react"
import { Modal, Button, FormGroup, ControlLabel, FormControl, HelpBlock } from "react-bootstrap"
import axios from "axios"
import getServerURL from "../../../../functions/getServerURL"
import { handleErrorFetchingData } from "../../../../functions/errorHandling"

export default function DueDateChangeModal({
  show,
  onHide,
  assignmentMetric,
  onSuccess
}) {
  const [newDueDate, setNewDueDate] = useState("")
  const [reason, setReason] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})
  const isEditable = assignmentMetric && assignmentMetric.result_id && assignmentMetric.can_edit_due_date !== false && assignmentMetric.status !== "Passed"

  // Initialize newDueDate when modal opens or assignmentMetric changes
  useEffect(() => {
    if (show && assignmentMetric && assignmentMetric.due) {
      setNewDueDate(assignmentMetric.due)
    }
  }, [show, assignmentMetric])

  const validateForm = () => {
    const newErrors = {}

    if (!newDueDate) {
      newErrors.newDueDate = "New due date is required"
    } else {
      // Check if the new due date is in the past
      const selectedDate = new Date(newDueDate)
      const today = new Date()
      today.setHours(0, 0, 0, 0) // Reset time to start of day for fair comparison

      if (selectedDate < today) {
        newErrors.newDueDate = "Due date cannot be in the past"
      }
    }

    if (!reason.trim()) {
      newErrors.reason = "Reason is required"
    } else if (reason.trim().length < 10) {
      newErrors.reason = "Reason must be at least 10 characters"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    // Safety guard: block submission in UI as well for passed or non-editable rows
    if (!isEditable) {
      alert("Cannot change due date after training is passed.")
      return
    }

    if (!validateForm()) {
      return
    }

    // Validate that assignmentMetric has a result_id before making API call
    if (!assignmentMetric || !assignmentMetric.result_id) {
      alert("Error: Assignment metric data is incomplete. Please try again.")
      return
    }

    setIsSubmitting(true)

    axios.patch(
      `${getServerURL()}results/${assignmentMetric.result_id}/update_due_date/`,
      {
        new_due_date: newDueDate,
        reason: reason.trim()
      },
      {
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
      }
    )
      .then(response => {
        // Verify response structure before proceeding
        if (!response || !response.data) {
          throw new Error("Invalid response from server")
        }

        alert("Due date updated successfully! An email notification has been sent to the user.")
        setNewDueDate(assignmentMetric && assignmentMetric.due ? assignmentMetric.due : "")
        setReason("")
        setErrors({})
        onHide()
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch(error => {
        if (error.response && error.response.data && error.response.data.message) {
          alert(`Error: ${error.response.data.message}`)
        } else {
          handleErrorFetchingData(error)
        }
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const handleClose = () => {
    setNewDueDate("")
    setReason("")
    setErrors({})
    onHide()
  }

  // Guard clause: Don't render modal content if assignmentMetric is not available
  if (!assignmentMetric) {
    return (
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Change Due Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Loading assignment details...</p>
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Change Due Date</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ marginBottom: "15px" }}>
          <strong>User:</strong> {assignmentMetric.user}<br/>
          <strong>Assessment:</strong> {assignmentMetric.assessment}<br/>
          <strong>Current Due Date:</strong> {assignmentMetric.due || "N/A"}
        </div>

        {!isEditable && (
          <div style={{ marginBottom: "10px", color: "#a94442" }}>
            Due date cannot be changed because this training has already been passed.
          </div>
        )}

        <FormGroup validationState={errors.newDueDate ? "error" : null}>
          <ControlLabel>New Due Date <span style={{ color: "red" }}>*</span></ControlLabel>
          <FormControl
            type="date"
            value={newDueDate}
            onChange={(e) => {
              setNewDueDate(e.target.value)
              if (errors.newDueDate) {
                setErrors(prev => ({ ...prev, newDueDate: undefined }))
              }
            }}
            disabled={isSubmitting || !isEditable}
          />
          {errors.newDueDate && <HelpBlock>{errors.newDueDate}</HelpBlock>}
        </FormGroup>

        <FormGroup validationState={errors.reason ? "error" : null}>
          <ControlLabel>Reason for Change <span style={{ color: "red" }}>*</span></ControlLabel>
          <FormControl
            componentClass="textarea"
            rows={4}
            placeholder="Please provide a detailed reason for changing the due date (minimum 10 characters)"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value)
              if (errors.reason) {
                const val = e.target.value.trim()
                if (val.length >= 10) {
                  setErrors(prev => ({ ...prev, reason: undefined }))
                } else if (val.length > 0) {
                  setErrors(prev => ({ ...prev, reason: "Reason must be at least 10 characters" }))
                }
              }
            }}
            disabled={isSubmitting || !isEditable}
          />
          {errors.reason && <HelpBlock>{errors.reason}</HelpBlock>}
          <HelpBlock>
            This reason will be recorded and included in the exported metrics report.
          </HelpBlock>
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button 
          bsStyle="primary" 
          onClick={handleSubmit} 
          disabled={isSubmitting || !isEditable}
        >
          {isSubmitting ? "Updating..." : "Update Due Date"}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}



// WEBPACK FOOTER //
// ./src/routes/qms-training/QMSTrainingManager/AssignmentMetricsContainer/DueDateChangeModal.js